import React, { useState, useEffect, useRef } from 'react';
import { diffLines } from 'diff';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, Grid, Button, Alert, Container, Card, CardContent, CircularProgress, Chip, IconButton, Tooltip, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions, Avatar } from '@mui/material';
import { ArrowBack as ArrowBackIcon, ExpandMore as ExpandMoreIcon, ChatBubbleOutline as ChatBubbleOutlineIcon, ContentCopy as ContentCopyIcon, Add as AddIcon, Remove as RemoveIcon, Build as BuildIcon, Person as PersonIcon, Folder as FolderIcon, InsertDriveFile as InsertDriveFileIcon, ChevronRight as ChevronRightIcon, ErrorOutline as ErrorOutlineIcon, CheckCircleOutline as CheckCircleOutlineIcon, PendingActions as PendingActionsIcon, CalendarMonth as CalendarMonthIcon, FolderOpen as FolderOpenIcon, Comment as CommentIcon } from '@mui/icons-material';
import axios from 'axios';
import API_BASE from '../apiBase';
import { format } from 'date-fns';
import useStagedFixes from '../hooks/useStagedFixes';
import useFileDiffs from '../hooks/useFileDiffs';
import useConfirmDialog from '../hooks/useConfirmDialog';
import ConfirmDialog from '../components/pr/ConfirmDialog';
import PRHeader from '../components/pr/PRHeader';
import PRToolbar from '../components/pr/PRToolbar';
import FileTree from '../components/pr/FileTree/FileTree';
import FileTreeBadges from '../components/pr/FileTree/FileTreeBadges';
import { buildAfterFixLineSet, buildIntentionalLineSet } from '../components/pr/DiffViewer/helpers';
import PRCommentsDialog from '../components/pr/Comments/PRCommentsDialog';
import FixDialog from '../components/pr/Fix/FixDialog';
import usePRFileTree from '../hooks/usePRFileTree';
import FileDiffPanel from '../components/pr/DiffViewer/FileDiffPanel';
import useSelectedFileBasePrefetch from '../hooks/useSelectedFileBasePrefetch';
import useFixWorkflow from '../hooks/useFixWorkflow';
import usePRDetails from '../hooks/usePRDetails';

function GitHubPRDetails() {
  const { owner, repo, prNumber, ownerAndRepo } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [ownerFinal, repoFinal] = ownerAndRepo
    ? decodeURIComponent(ownerAndRepo).split('/')
    : [owner, repo];

  const organization = ownerFinal;
  const project = repoFinal;
  const repositoryID = repoFinal;
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [fixLoading, setFixLoading] = useState(false);
  const [fixedFile, setFixedFile] = useState(null);
  const [fixToken, setFixToken] = useState(null);
  const [pendingFixMeta, setPendingFixMeta] = useState(null);
  const [prCommentsOpen, setPrCommentsOpen] = useState(false);
  const { confirmState, openConfirm, closeConfirm } = useConfirmDialog();
  const [commitLoading, setCommitLoading] = useState(false);
  const [fixingFilePath, setFixingFilePath] = useState('');
  const bgControllersRef = useRef(new Set());
  const fixControllersRef = useRef(new Set());
  
  const getProjectKey = () => {
    const params = new URLSearchParams(location.search);
    const key = params.get('project_key') || sessionStorage.getItem('qdex:lastProjectKey') || '';
    return key;
  };

  const storageKey = `qdex:stagedFixes:github:${organization}/${project}/${prNumber}`;
  const {
    appliedFixes,
    setAppliedFixes,
    appliedTokens,
    setAppliedTokens,
    intentionalStaged,
    setIntentionalStaged,
    persistStaged,
    rehydrateStaged,
  } = useStagedFixes(storageKey, { ttlMs: 30 * 60 * 1000 });

  const pageScrollRef = useRef(null);
  const contentScrollRef = useRef(null);
  const contentScrollRightRef = useRef(null);
  const [scrolledIntoPanes, setScrolledIntoPanes] = useState(false);
  const selectedPathRef = useRef('');
  const prefetchStartedRef = useRef(false);
  const appBarHeight = 56;
  const prBarHeight = 48;
  const paneHeight = `calc(100vh - ${appBarHeight + prBarHeight}px)`;

  const { fileCache, setFileCache, loadingFileMap, setLoadingFileMap, ensureFileLoaded, abortAll } = useFileDiffs({
    provider: 'github',
    identifiers: { organization, project, repositoryID, prNumber },
    getProjectKey,
  });

  const {
    prDetails,
    setPrDetails,
    loading,
    error,
    setError,
    fileTree,
    expandedNodes,
    setExpandedNodes,
    selectedFilePath,
    setSelectedFilePath,
    fetchPRDetails,
  } = usePRDetails({ provider: 'github', identifiers: { organization, project, repositoryID, prNumber }, getProjectKey, fileCache });
  useEffect(() => { selectedPathRef.current = selectedFilePath; }, [selectedFilePath]);
  useEffect(() => { prefetchStartedRef.current = false; }, [organization, project, prNumber]);
  useEffect(() => { rehydrateStaged(); }, [organization, project, prNumber]);
  useEffect(() => {
    return () => {
      try {
        bgControllersRef.current.forEach((c) => { try { c.abort(); } catch {} });
        bgControllersRef.current.clear();
      } catch {}
      try {
        fixControllersRef.current.forEach((c) => { try { c.abort(); } catch {} });
        fixControllersRef.current.clear();
      } catch {}
    };
  }, []);

  const { normalizePath, buildFileTree, getAllFilePathsFromTree } = usePRFileTree();

  const ensureFileLoadedLocal = async (path, options = {}) => {
    const { trackAbort = false } = options;
    const key = normalizePath(path || '');
    const already = (fileCache && fileCache[key]);
    const inDetails = (prDetails?.files || []).find(f => normalizePath(f.path || f.filename || '') === key);
    const hasDiff = !!(inDetails && Array.isArray(inDetails.diff_lines) && inDetails.diff_lines.length > 0);
    if (already || hasDiff || !key || loadingFileMap[key]) return;
    try {
      setLoadingFileMap(prev => ({ ...prev, [key]: true }));
      const projectKey = getProjectKey(); if (!projectKey) return;
      const controller = trackAbort ? new AbortController() : null;
      if (controller) bgControllersRef.current.add(controller);
      try {
        const { data } = await axios.get(`${API_BASE}/api/github/pull-requests/${encodeURIComponent(organization)}/${encodeURIComponent(project)}/${prNumber}/file`, { params: { path: key }, headers: { 'X-API-KEY': projectKey }, signal: controller ? controller.signal : undefined });
        setFileCache(prev => ({ ...prev, [key]: { diff_lines: data?.diff_lines || [], comments: data?.comments || [] } }));
        setPrDetails(prev => {
          if (!prev) return prev;
          const files = Array.isArray(prev.files) ? [...prev.files] : [];
          const idx = files.findIndex(f => normalizePath(f.path || f.filename || '') === key);
          if (idx >= 0) {
            const curr = files[idx] || {};
            const nextDiff = data?.diff_lines || curr.diff_lines || [];
            const nextComments = data?.comments || curr.comments || [];
            const sameLen = Array.isArray(curr.diff_lines) && curr.diff_lines.length === nextDiff.length;
            const sameComm = Array.isArray(curr.comments) && curr.comments.length === nextComments.length;
            if (!sameLen || !sameComm) { files[idx] = { ...curr, diff_lines: nextDiff, comments: nextComments }; }
          }
          return { ...prev, files };
        });
      } catch (error) {
        if (error && (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || (typeof axios.isCancel === 'function' && axios.isCancel(error)))) {
          // ignore cancelled
        } else {
          // swallow other errors
        }
      } finally {
        if (controller) { try { bgControllersRef.current.delete(controller); } catch {} }
      }
    } catch {}
    finally { setLoadingFileMap(prev => { const next = { ...prev }; delete next[key]; return next; }); }
  };

  useEffect(() => {
    const sel = normalizePath(selectedFilePath || '');
    if (!sel) return;
    const file = (prDetails?.files || []).find(f => normalizePath(f.path || f.filename || '') === sel);
    const hasDiff = !!(file && Array.isArray(file.diff_lines) && file.diff_lines.length > 0);
    if (!hasDiff) { ensureFileLoadedLocal(sel); }
  }, [selectedFilePath, prDetails]);

  useSelectedFileBasePrefetch({
    API_BASE,
    organization,
    project,
    prNumber,
    getProjectKey,
    prDetails,
    selectedFilePath,
    normalizePath,
    fileTree,
    getAllFilePathsFromTree,
    loadingFileMap,
    fileCache,
    ensureFileLoaded: ensureFileLoadedLocal,
  });

  const {
    handleFixRequest,
    handleMarkIntentional,
    handleFixFileRequest,
    handleCommitFix,
    handleDiscardFix,
    handleCommitAll,
    handleDiscardAll,
    handleDiscardApplied,
    handleViewAppliedFix,
  } = useFixWorkflow({
    provider: 'github',
    API_BASE,
    identifiers: { organization, project, repositoryID, prNumber },
    getProjectKey,
    normalizePath,
    refs: { fixControllersRef },
    state: {
      appliedFixes,
      setAppliedFixes,
      appliedTokens,
      setAppliedTokens,
      intentionalStaged,
      setIntentionalStaged,
      setError,
      setLoadingFileMap,
      setFileCache,
      setPrDetails,
    },
    ui: { setFixDialogOpen, setFixLoading, setFixedFile, setFixToken, setPendingFixMeta },
    storageKey,
  });

  const toggleDir = (path) => setExpandedNodes((prev) => ({ ...prev, [path]: !prev[path] }));

  const abortAllFixRequests = () => {
    try {
      fixControllersRef.current.forEach((c) => { try { c.abort(); } catch {} });
      fixControllersRef.current.clear();
    } catch {}
    abortAll();
  };

  const { open: sidebarOpen, drawerWidth: sidebarWidth } = React.useContext(require('../components/SidebarContext').SidebarContext);
  const effectiveWidth = sidebarOpen ? sidebarWidth : 50;
  const appBarTop = 65;
  const commonStyles = { backgroundColor: '#0d1117', minHeight: '90vh', color: '#f0f6fc', m: 0, p: 0, position: 'fixed', top: `${appBarTop}px`, left: 0, right: 0, bottom: 0, zIndex: 1000, overflow: 'auto' };
  const contentWrapperStyles = { ml: { xs: 0, md: `${effectiveWidth}px` }, width: { xs: '100%', md: `calc(100% - ${effectiveWidth}px)` }, display: 'block' };
  const containerStyles = { mt: 0, mb: 1, backgroundColor: '#0d1117', px: 2, pt: 0.5, pb: 1.5, m: 0, maxWidth: 'none', minHeight: '90vh', display: 'flex', flexDirection: 'column', width: '100%', boxSizing: 'border-box' };

  useEffect(() => {
    const left = contentScrollRef.current; const right = contentScrollRightRef.current; const page = pageScrollRef.current;
    let lastLeftTop = 0; let lastRightTop = 0;
    const onScrollAny = () => {
      const yLeft = left ? left.scrollTop || 0 : 0; const yRight = right ? right.scrollTop || 0 : 0; const yPage = page ? page.scrollTop || 0 : 0;
      const shouldStick = yPage > 0 || ((yLeft > 0 || yRight > 0) && yPage > 0);
      setScrolledIntoPanes(shouldStick);
      const scrolledDownLeft = yLeft > lastLeftTop; const scrolledDownRight = yRight > lastRightTop;
      if (scrolledDownLeft || scrolledDownRight) {
        if (page) { const max = page.scrollHeight - page.clientHeight; if (max > 0 && page.scrollTop < max) { page.scrollTop = max; } }
        const root = document.scrollingElement || document.documentElement || document.body;
        if (root) { const maxWin = root.scrollHeight - root.clientHeight; if (maxWin > 0 && root.scrollTop < maxWin) { root.scrollTop = maxWin; } }
      }
      lastLeftTop = yLeft; lastRightTop = yRight;
    };
    const onInnerWheel = (e) => { if (e && e.deltaY > 0 && page) { const max = page.scrollHeight - page.clientHeight; if (max > 0) page.scrollTop = max; const root = document.scrollingElement || document.documentElement || document.body; if (root) { const maxWin = root.scrollHeight - root.clientHeight; if (maxWin > 0) root.scrollTop = maxWin; } } };
    left && left.addEventListener('scroll', onScrollAny, { passive: true }); right && right.addEventListener('scroll', onScrollAny, { passive: true }); page && page.addEventListener('scroll', onScrollAny, { passive: true });
    left && left.addEventListener('wheel', onInnerWheel, { passive: true }); right && right.addEventListener('wheel', onInnerWheel, { passive: true });
    return () => { left && left.removeEventListener('scroll', onScrollAny); right && right.removeEventListener('scroll', onScrollAny); page && page.removeEventListener('scroll', onScrollAny); left && left.removeEventListener('wheel', onInnerWheel); right && right.removeEventListener('wheel', onInnerWheel); };
  }, []);

  if (loading) return (<Box sx={{ ...commonStyles }}><Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><CircularProgress sx={{ color: '#58a6ff' }} /></Box></Box>);
  if (error) return (
    <Box sx={{ ...commonStyles }}>
      <Box sx={{ ...contentWrapperStyles }}>
        <Container maxWidth={false} sx={{ ...containerStyles }}>
          <Alert severity="error" sx={{ mt: 4, backgroundColor: '#67060c', color: '#f85149', border: '1px solid #f85149', whiteSpace: 'pre-line' }}>{error}</Alert>
          <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
            <Button onClick={() => navigate('/app/projects')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Open Projects to Update Token</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
  if (!prDetails) return (
    <Box sx={{ ...commonStyles }}>
      <Box sx={{ ...contentWrapperStyles }}>
        <Container maxWidth={false} sx={{ ...containerStyles }}>
          <Alert severity="info" sx={{ mt: 4, backgroundColor: '#0c532d', color: '#7ee787', border: '1px solid #3fb950' }}>No PR details available.</Alert>
          <Box sx={{ mt: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );

  try { /* removed debug logs */ } catch (e) {}
  const isPRActive = ((prDetails?.status || '').toString().toLowerCase() === 'active');

  return (
    <Box ref={pageScrollRef} sx={{ ...commonStyles }}>
      <Box sx={{ ...contentWrapperStyles }}>
        <Container maxWidth={false} sx={{ ...containerStyles }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/pull-requests')} sx={{ color: '#58a6ff', '&:hover': { backgroundColor: '#58a6ff20' } }}>Back to List</Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', color: '#f0f6fc' }}>PR #{prNumber}</Typography>
          </Box>
          {(() => (
            <PRHeader
              title={prDetails.title}
              status={prDetails.status}
              repositoryLabel={`${organization}/${project}`}
              author={prDetails.author}
              createdLabel={prDetails.created_at ? format(new Date(prDetails.created_at), 'PPP p') : 'Unknown'}
              filesCount={prDetails.files?.length || 0}
              commentsCount={prDetails.total_comments || 0}
              onViewPRComments={() => setPrCommentsOpen(true)}
            />
          ))()}

          {(() => {
            const issuesCount = (prDetails?.comments || []).filter(c => !!c.file).length;
            const fixedCount = (prDetails?.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('fixed - ')).length;
            const intentionalCount = (prDetails?.comments || []).filter(c => (c.body || '').toString().toLowerCase().includes('marked intentional - ')).length;
            const stagedIntent = Object.values(intentionalStaged || {}).reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
            const stagedCount = appliedTokens.length + stagedIntent;
            const commitDisabled = (((appliedTokens.length === 0) && (stagedIntent === 0)) || commitLoading);
            const layout = scrolledIntoPanes ? { top: appBarHeight, left: (typeof effectiveWidth === 'number' ? `${effectiveWidth}px` : 0), width: { xs: '100%', md: `calc(100% - ${effectiveWidth}px)` }, right: 0 } : { };
            return (
              <PRToolbar
                issuesCount={issuesCount}
                fixedCount={fixedCount}
                intentionalCount={intentionalCount}
                stagedCount={stagedCount}
                onDiscardAll={() => openConfirm('Discard changes?', 'Are you sure you want to discard all staged fixes and intentional marks?', () => { closeConfirm(); handleDiscardAll(); })}
                onCommitAll={() => openConfirm('Commit changes?', 'Are you sure you want to commit all staged fixes and intentional marks?', async () => { setCommitLoading(true); try { await handleCommitAll(); } finally { setCommitLoading(false); } closeConfirm(); })}
                commitDisabled={commitDisabled}
                committing={commitLoading}
                height={prBarHeight}
                sticked={scrolledIntoPanes}
                layout={layout}
              />
            );
          })()}
          {scrolledIntoPanes && <Box sx={{ height: prBarHeight }} />}

          <Card sx={{ backgroundColor: '#161b22', border: '1px solid #30363d', borderRadius: '6px', flex: 1, position: 'sticky', top: appBarHeight + prBarHeight, zIndex: 1 }}>
            <CardContent sx={{ p: 0 }}>
              <Grid container>
                <Grid item xs={12} md={4} lg={3} sx={{ borderRight: '1px solid #30363d' }}>
                  <Box ref={contentScrollRef} sx={{ backgroundColor: '#0d1117', height: scrolledIntoPanes ? `calc(100vh - ${appBarHeight + prBarHeight}px)` : paneHeight, overflowY: 'auto', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { backgroundColor: '#0d1117' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#30363d', borderRadius: '8px', border: '2px solid #0d1117' }, scrollbarWidth: 'thin', scrollbarColor: '#30363d #0d1117' }}>
                    <Box sx={{ position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#0d1117', borderBottom: '1px solid #30363d', px: 2, py: 1.5 }}>
                      <Typography variant="subtitle2" sx={{ color: '#7d8590', fontWeight: 600 }}>Files changed</Typography>
                    </Box>
                    <Box sx={{ px: 1.5, pt: 1 }}>
                      <FileTree
                        fileTree={fileTree}
                        expandedNodes={expandedNodes}
                        selectedFilePath={selectedFilePath}
                        normalizePath={normalizePath}
                        onToggleDir={(p) => toggleDir(p)}
                        onSelectFile={(p) => setSelectedFilePath(p)}
                        badgesForPath={(childKey) => {
                          const latestFile = (prDetails.files || []).find(f => normalizePath(f.path || f.filename || '') === childKey);
                          return <FileTreeBadges latestFile={latestFile} appliedFixes={appliedFixes} intentionalStaged={intentionalStaged} />;
                        }}
                      />
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={8} lg={9}>
                  <Box ref={contentScrollRightRef} sx={{ height: scrolledIntoPanes ? `calc(100vh - ${appBarHeight + prBarHeight}px)` : paneHeight, overflowY: 'auto', '&::-webkit-scrollbar': { width: '12px' }, '&::-webkit-scrollbar-track': { backgroundColor: '#0d1117' }, '&::-webkit-scrollbar-thumb': { backgroundColor: '#30363d', borderRadius: '8px', border: '2px solid #0d1117' }, scrollbarWidth: 'thin', scrollbarColor: '#30363d #0d1117' }}>
                    {(() => {
                      const file = (prDetails.files || []).find(f => {
                        const fp = normalizePath(f.path || f.filename || '');
                        const sel = normalizePath(selectedFilePath);
                        if (fp === sel) return true; const fpName = fp.split('/').pop(); return fpName === sel || sel.split('/').pop() === fpName;
                      });
                      if (!file) return <Typography sx={{ color: '#7d8590' }}>Select a file to view changes.</Typography>;
                      if (Array.isArray(file.diff_lines) && file.diff_lines.length > 0) {
                      return (
                          <FileDiffPanel
                            file={file}
                            isPRActive={isPRActive}
                            appliedFixes={appliedFixes}
                            intentionalStaged={intentionalStaged}
                            onFixFile={handleFixFileRequest}
                            onDiscardApplied={handleDiscardApplied}
                            onViewAppliedFix={handleViewAppliedFix}
                            fixingFilePath={fixingFilePath}
                            onFixRequest={handleFixRequest}
                            onMarkIntentional={handleMarkIntentional}
                            buildAfterFixLineSet={buildAfterFixLineSet}
                            buildIntentionalLineSet={buildIntentionalLineSet}
                            selectedFilePath={selectedFilePath}
                          />
                        );
                      }
                              const currentPath = normalizePath(file.path || file.filename || '');
                              const isLoading = !!loadingFileMap[currentPath];
                              return isLoading ? (
                                <Box sx={{ p: 6, textAlign: 'center', color: '#7d8590', backgroundColor: '#0d1117' }}>
                                  <CircularProgress size={20} sx={{ color: '#58a6ff', mr: 1 }} />
                                  <Typography variant="body2" component="span" sx={{ color: '#7d8590' }}>Loading file diff…</Typography>
                                </Box>
                              ) : (
                                <Box sx={{ p: 6, textAlign: 'center', color: '#7d8590', backgroundColor: '#0d1117' }}>
                                  <Typography variant="body2" component="span" sx={{ color: '#7d8590' }}>This file was deleted.</Typography>
                                </Box>
                      );
                    })()}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <PRCommentsDialog
            open={prCommentsOpen}
            onClose={() => setPrCommentsOpen(false)}
            comments={prDetails?.comments || []}
          />

          <ConfirmDialog
            open={confirmState.open}
            title={confirmState.title}
            message={confirmState.message}
            onClose={closeConfirm}
            onConfirm={() => { if (typeof confirmState.onConfirm === 'function') confirmState.onConfirm(); }}
            loading={commitLoading}
          />

          <FixDialog
            open={fixDialogOpen}
            loading={fixLoading}
            fixedFile={fixedFile}
            fixToken={fixToken}
            isPRActive={isPRActive}
            onDiscard={() => handleDiscardFix({ fixToken, fixedFile })}
            onApply={() => handleCommitFix({ fixedFile, fixToken, pendingFixMeta })}
            onClose={() => { abortAllFixRequests(); setFixLoading(false); setFixDialogOpen(false); }}
          />
        </Container>
      </Box>
    </Box>
  );
}

export default GitHubPRDetails;



